import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Alert, Avatar, AvatarGroup, Box, Button, Chip, IconButton, MenuItem, Paper, Popover, Select, Skeleton, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  CalendarViewMonth as MonthIcon,
  ViewAgenda as ListIcon,
  Add as AddIcon,
  Videocam as MeetingIcon,
  Assignment as DeadlineIcon,
  Event as EventIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { useMyLabs } from 'shared/hooks/useMyLabs';
import PageContainer from 'components/common/layout/PageContainer';
import PageHeader from 'components/common/layout/PageHeader';
import EventFormDialog from './EventFormDialog';
import CalendarSyncDialog from './CalendarSyncDialog';
import { typeColor } from './eventMeta';

const MEETING_COLOR = '#0f766e';
const DEADLINE_COLOR = '#dc2626';
const BUSY_COLOR = '#9e9e9e';
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_PER_DAY = 3;

// The toggles above the calendar: which of my plans to show
const KINDS = [
  { key: 'hosting',    label: 'Hosting' },
  { key: 'going',      label: 'Going' },
  { key: 'interested', label: 'Interested' },
  { key: 'invited',    label: 'Invited' },
  { key: 'meeting',    label: 'Meetings' },
  { key: 'deadline',   label: 'Deadlines' },
];

// On a lab calendar: the lab's own events, what members go to or are interested in, and "Busy" (an event
// of theirs you may not open)
const LAB_KINDS = [
  { key: 'lab',        label: 'Lab events' },
  { key: 'going',      label: 'Going' },
  { key: 'interested', label: 'Interested' },
  { key: 'busy',       label: 'Busy' },
];

const kindOf = (item) => {
  if (item.people) {
    if (item.kind === 'busy') return 'busy';
    if (item.lab_event) return 'lab';
    return item.people.some((p) => p.rsvp === 'going') ? 'going' : 'interested';
  }
  if (item.kind === 'meeting' || item.kind === 'deadline') return item.kind;
  if (item.hosting) return 'hosting';
  return item.rsvp || 'going';
};

const colorOf = (item) => (item.kind === 'meeting' ? MEETING_COLOR
  : item.kind === 'deadline' ? DEADLINE_COLOR : item.kind === 'busy' ? BUSY_COLOR : typeColor(item.event_type));

// "Ayşe, Mehmet +2": who on a lab calendar
const firstNames = (people, max = 2) => {
  const names = (people || []).map((p) => p.name.split(' ')[0]);
  return names.length > max ? `${names.slice(0, max).join(', ')} +${names.length - max}` : names.join(', ');
};
const pillText = (item) => (item.people?.length ? `${firstNames(item.people)} · ${item.title}` : item.title);

const startOfGrid = (month) => {
  const first = month.startOf('month');
  return first.subtract((first.day() + 6) % 7, 'day');        // back to Monday
};

// Does ITEM fall on DAY? Multi-day events show on every day they run.
const onDay = (item, day) => {
  const start = dayjs(item.start).startOf('day');
  const end = item.end ? dayjs(item.end).startOf('day') : start;
  return !day.isBefore(start) && !day.isAfter(end);
};

const timeLabel = (item) => {
  if (item.kind === 'deadline') return 'Deadline';
  const s = dayjs(item.start);
  const multiDay = item.end && !dayjs(item.end).isSame(s, 'day');
  if (multiDay) return `${s.format('D MMM')} – ${dayjs(item.end).format('D MMM')}`;
  if (s.hour() === 0 && s.minute() === 0) return 'All day';
  return item.end ? `${s.format('HH:mm')}–${dayjs(item.end).format('HH:mm')}` : s.format('HH:mm');
};

/** A pill for one plan: solid when going or hosting, outlined when only interested, dashed when invited. */
function ItemPill({ item, onOpen, dense }) {
  const color = colorOf(item);
  const kind = kindOf(item);
  const solid = ['hosting', 'going', 'meeting', 'deadline', 'lab', 'busy'].includes(kind);
  const who = item.people?.length ? ` · ${item.people.map((p) => `${p.name} (${p.rsvp})`).join(', ')}` : '';
  return (
    <Tooltip title={`${item.title}${item.location ? ` · ${item.location}` : ''} · ${timeLabel(item)}${who}`} disableInteractive>
      <Box
        onClick={(e) => { e.stopPropagation(); onOpen(item); }}
        sx={{
          px: 0.75, py: dense ? 0.1 : 0.25, mb: 0.4, borderRadius: 1, cursor: item.url ? 'pointer' : 'default',
          fontSize: '0.92rem', lineHeight: 1.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          bgcolor: solid ? color : alpha(color, 0.08), color: solid ? '#fff' : color,
          border: '1px solid', borderColor: color, borderStyle: kind === 'invited' ? 'dashed' : 'solid',
          textDecoration: item.cancelled ? 'line-through' : 'none', opacity: item.cancelled ? 0.6 : 1,
        }}
      >
        {pillText(item)}
      </Box>
    </Tooltip>
  );
}

function AgendaRow({ item, onOpen, kinds }) {
  const color = colorOf(item);
  const Icon = item.kind === 'meeting' ? MeetingIcon : item.kind === 'deadline' ? DeadlineIcon : EventIcon;
  const kind = kindOf(item);
  return (
    <Box onClick={() => onOpen(item)} sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: 2, cursor: item.url ? 'pointer' : 'default',
      borderLeft: '4px solid', borderLeftColor: color, '&:hover': { bgcolor: 'action.hover' },
    }}>
      <Icon sx={{ color, fontSize: 20 }} />
      <Box sx={{ width: 96, flexShrink: 0 }}>
        <Typography variant="body2" color="text.secondary">{timeLabel(item)}</Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ textDecoration: item.cancelled ? 'line-through' : 'none' }}>
          {item.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {[item.event_type_display, item.location].filter(Boolean).join(' · ')}
        </Typography>
      </Box>
      {item.people?.length > 0 && (
        <Tooltip title={item.people.map((p) => `${p.name} (${p.rsvp})`).join(', ')}>
          <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 26, height: 26, fontSize: 12 } }}>
            {item.people.map((p) => <Avatar key={p.id} src={p.avatar_url || undefined} alt={p.name}>{p.name[0]}</Avatar>)}
          </AvatarGroup>
        </Tooltip>
      )}
      <Chip size="small" variant="outlined" label={item.cancelled ? 'Cancelled' : kinds.find((k) => k.key === kind)?.label} />
    </Box>
  );
}

/** My calendar: the events I host or answered, their paper deadlines, and my scheduled meetings. With
 *  ?lab=<id>, a lab's calendar instead: what its members have planned (see event/calendar.py lab_items). */
export default function MyCalendarPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const labs = useMyLabs();
  const labId = searchParams.get('lab') || '';
  const lab = labs.find((l) => String(l.id) === labId);
  const kinds = labId ? LAB_KINDS : KINDS;
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const [month, setMonth] = useState(dayjs().startOf('month'));
  const [view, setView] = useState(narrow ? 'list' : 'month');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shown, setShown] = useState(() => new Set([...KINDS, ...LAB_KINDS].map((k) => k.key)));
  const [dayPopover, setDayPopover] = useState(null);    // { anchor, day }
  const [createOpen, setCreateOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);

  const gridStart = useMemo(() => startOfGrid(month), [month]);
  const days = useMemo(() => Array.from({ length: 42 }, (_, i) => gridStart.add(i, 'day')), [gridStart]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const range = { start: gridStart.format('YYYY-MM-DD'), end: gridStart.add(41, 'day').format('YYYY-MM-DD') };
    axiosService.get(labId ? '/events/lab-calendar/' : '/events/calendar/', { params: labId ? { ...range, lab: labId } : range })
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => setError(apiErrorMessage(err, 'Could not load the calendar.')))
      .finally(() => setLoading(false));
  }, [gridStart, labId]);

  useEffect(() => { load(); }, [load]);

  const visible = items.filter((i) => shown.has(kindOf(i)));
  const counts = kinds.reduce((acc, k) => ({ ...acc, [k.key]: items.filter((i) => kindOf(i) === k.key).length }), {});
  const open = (item) => { if (item.url) navigate(item.url); };
  const toggleKind = (key) => setShown((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // The list view: this month's days that have something, in order
  const agenda = days
    .filter((d) => d.month() === month.month())
    .map((d) => ({ day: d, items: visible.filter((i) => onDay(i, d)) }))
    .filter((g) => g.items.length);

  const today = dayjs();

  return (
    <PageContainer width="wide">
      {/* month navigation after the title; view and create at the right: the Collaboration Hub's header */}
      <PageHeader title={labId ? `${lab?.name || 'Lab'} Calendar` : 'My Calendar'} sx={{ mb: 2 }} actions={(
        <>
          {labs.length > 0 && (
            <Select size="small" value={labId} displayEmpty aria-label="Whose calendar"
              onChange={(e) => setSearchParams(e.target.value ? { lab: e.target.value } : {})}
              sx={{ minWidth: 180, height: 32, fontSize: '0.9rem' }}>
              <MenuItem value="">My calendar</MenuItem>
              {labs.map((l) => <MenuItem key={l.id} value={String(l.id)}>{l.name}</MenuItem>)}
            </Select>
          )}
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
            <ToggleButton value="month" aria-label="Month view"><MonthIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="list" aria-label="List view"><ListIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
          {!labId && (<Tooltip title="Sync with Google, Apple or Outlook">
            <IconButton onClick={() => setSyncOpen(true)} aria-label="Sync with your calendar app"
              sx={{ color: '#764ba2', bgcolor: alpha('#764ba2', 0.08), '&:hover': { bgcolor: alpha('#764ba2', 0.16) } }}>
              <SyncIcon />
            </IconButton>
          </Tooltip>)}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap', bgcolor: '#764ba2', '&:hover': { bgcolor: '#5b3680' } }}
          >
            Create
          </Button>
        </>
      )}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
          <IconButton size="small" aria-label="Previous month" onClick={() => setMonth(month.subtract(1, 'month'))}><PrevIcon /></IconButton>
          <Typography variant="subtitle1" fontWeight={700} sx={{ minWidth: 130, textAlign: 'center' }}>{month.format('MMMM YYYY')}</Typography>
          <IconButton size="small" aria-label="Next month" onClick={() => setMonth(month.add(1, 'month'))}><NextIcon /></IconButton>
          <Button size="small" onClick={() => setMonth(dayjs().startOf('month'))} disabled={month.isSame(today, 'month')}>
            Today
          </Button>
        </Box>
      </PageHeader>

      {/* Which plans to show */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2, ml:3 }}>
        {/* <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Show:</Typography> */}
        {kinds.map((k) => {
          const on = shown.has(k.key);
          return (
            <Button key={k.key} onClick={() => toggleKind(k.key)} aria-pressed={on}
              sx={{
                borderRadius: '20px', fontWeight: 500, fontSize: '.95rem', height: 30, px: '12px', minWidth: 0,
                textTransform: 'none', color: on ? 'primary.main' : 'text.disabled',
                bgcolor: (t) => alpha(t.palette.primary.main, on ? 0.1 : 0.03),
                '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, on ? 0.16 : 0.08) },
              }}>
              {k.label}{counts[k.key] ? ` · ${counts[k.key]}` : ''}
            </Button>
          );
        })}
      </Box>

      {/* {labId && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -1, mb: 2 }}>
          The lab's events and what its members are going to or interested in. Members can keep an event to
          themselves on its page; events you cannot open show only as Busy.
        </Typography>
      )} */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Skeleton variant="rounded" height={560} sx={{ borderRadius: 3 }} />
      ) : view === 'month' ? (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
            {WEEKDAYS.map((w) => (
              <Box key={w} sx={{ py: 1, textAlign: 'center', borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">{w}</Typography>
              </Box>
            ))}
            {days.map((d, i) => {
              const dayItems = visible.filter((it) => onDay(it, d));
              const inMonth = d.month() === month.month();
              const isToday = d.isSame(today, 'day');
              return (
                <Box key={d.format('YYYY-MM-DD')} sx={{
                  minHeight: { xs: 72, md: 112 }, p: 0.5, minWidth: 0,
                  borderRight: (i % 7) < 6 ? 1 : 0, borderBottom: i < 35 ? 1 : 0, borderColor: 'divider',
                  bgcolor: inMonth ? 'background.paper' : 'action.hover',
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.25 }}>
                    <Typography variant="caption" sx={{
                      width: 24, height: 24, lineHeight: '24px', textAlign: 'center', borderRadius: '50%',
                      fontWeight: isToday ? 700 : 400, color: isToday ? '#fff' : inMonth ? 'text.primary' : 'text.disabled',
                      bgcolor: isToday ? '#764ba2' : 'transparent',
                    }}>
                      {d.date()}
                    </Typography>
                  </Box>
                  {dayItems.slice(0, MAX_PER_DAY).map((it) => <ItemPill key={it.id} item={it} onOpen={open} dense={narrow} />)}
                  {dayItems.length > MAX_PER_DAY && (
                    <Typography variant="caption" color="primary" sx={{ cursor: 'pointer', pl: 0.5, fontWeight: 600 }}
                      onClick={(e) => setDayPopover({ anchor: e.currentTarget, day: d })}>
                      +{dayItems.length - MAX_PER_DAY} more
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Paper>
      ) : agenda.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10, color: 'text.secondary' }}>
          <EventIcon sx={{ fontSize: 56, opacity: 0.25, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">Nothing planned in {month.format('MMMM')}</Typography>
          <Typography variant="body2" mt={0.5}>
            Answer Going or Interested on an event, or create one, and it shows up here.
          </Typography>
          <Button sx={{ mt: 2, textTransform: 'none' }} onClick={() => navigate('/events/')}>Browse events</Button>
        </Box>
      ) : (
        <Box>
          {agenda.map(({ day, items: dayItems }) => (
            <Box key={day.format('YYYY-MM-DD')} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700}
                color={day.isSame(today, 'day') ? '#764ba2' : 'text.secondary'} sx={{ mb: 0.5 }}>
                {day.format('dddd, D MMMM')}{day.isSame(today, 'day') ? ' · Today' : ''}
              </Typography>
              <Paper variant="outlined" sx={{ borderRadius: 2, p: 0.5 }}>
                {dayItems.map((it) => <AgendaRow key={it.id} item={it} onOpen={open} kinds={kinds} />)}
              </Paper>
            </Box>
          ))}
        </Box>
      )}

      {!loading && view === 'month' && items.length === 0 && !error && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          Nothing planned in these weeks. Answer Going or Interested on an event, or create one, and it shows up here.
        </Typography>
      )}

      <Popover open={Boolean(dayPopover)} anchorEl={dayPopover?.anchor} onClose={() => setDayPopover(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        {dayPopover && (
          <Box sx={{ p: 1.5, width: 260 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{dayPopover.day.format('dddd, D MMMM')}</Typography>
            {visible.filter((it) => onDay(it, dayPopover.day)).map((it) => (
              <ItemPill key={it.id} item={it} onOpen={(x) => { setDayPopover(null); open(x); }} />
            ))}
          </Box>
        )}
      </Popover>

      <EventFormDialog open={createOpen} onClose={() => setCreateOpen(false)} onSaved={load} />
      <CalendarSyncDialog open={syncOpen} onClose={() => setSyncOpen(false)} />
    </PageContainer>
  );
}
